/**
 * `/auth?t=<token>` — the landing spot for a magic link (spec §11).
 *
 * The exchange runs exactly once per mount. A second attempt would be pointless as well
 * as alarming: the token is single-use, so a retry can only ever report failure, and
 * React 19's StrictMode double-invokes effects in development.
 */

import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { signInWithMagicToken, useAuth, type ExchangeError } from "../auth.js";

export function AuthPage() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const { user } = useAuth();
  const [error, setError] = useState<ExchangeError | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    void signInWithMagicToken(token).then(setError);
  }, [token]);

  if (user) return <Navigate to="/" replace />;

  if (!token) {
    return (
      <div className="centered">
        <h1 className="title">That link is missing its token.</h1>
        <p className="sub">Message <b>dashboard</b> to the bot for a new one.</p>
      </div>
    );
  }

  if (error === "network") {
    return (
      <div className="centered">
        <h1 className="title">Couldn't reach the server.</h1>
        <p className="sub">Check your connection and open the link again.</p>
      </div>
    );
  }

  if (error === "invalid") {
    // Expired, already used, or mistyped — the endpoint answers all three identically
    // (D-59), and the advice is the same in every case.
    return (
      <div className="centered">
        <h1 className="title">This link is spent.</h1>
        <p className="sub">
          Links work once and last 15 minutes. Message <b>dashboard</b> to the bot for a
          fresh one.
        </p>
      </div>
    );
  }

  return <p className="sub centered">Signing you in…</p>;
}
